import React, { useMemo, useRef, useState } from 'react';

import { formatPaise } from '../../../utils/money';
import { describeBalance } from './billModel';

const overlayCls = 'fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4';
const panelCls = 'bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl shadow-xl w-full max-w-md max-h-[92vh] overflow-y-auto';
const btnPrimary = 'px-4 py-2 rounded-lg bg-teal-600 text-white text-sm font-semibold disabled:opacity-60';
const btnSecondary = 'px-4 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100 text-sm';
const inputCls = 'w-full px-2.5 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-100 text-sm';
const labelCls = 'block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1';

/** New idempotency key per dialog open → a double-click posts ONE waiver (replay). */
function newKey() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return `concession:${crypto.randomUUID()}`;
  return `concession:${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

/**
 * WaiverConcessionDialog (C4 defect fix) — grant a bill-side waiver/concession.
 * SHARED between the running-bill panel and the desk row's quick action so the
 * two surfaces cannot drift. Admin-only (the parent perm-gates the entry action
 * on GRANT_CONCESSION). Distinct from "Refund deposit" — a waiver writes a
 * credit charge line and LOWERS the balance owed; deposits are untouched.
 *
 * Reason is mandatory (gates Save). `netChargesPaise`, when the caller has it
 * (the running-bill panel does; the desk row's list summary does not), is a
 * CLIENT HINT only — the server is the authoritative cap and returns
 * CONCESSION_EXCEEDS_CHARGES with the true `remaining_paise` on a race, which
 * this dialog surfaces inline while keeping the entered values. Save is
 * single-flight + a fresh idempotency key per open. In-app modal — no
 * window.confirm/alert.
 *
 * @param onSubmit(body) → { success, data?, error?, code?, remaining_paise? }
 */
export default function WaiverConcessionDialog({ admission, balancePaise, netChargesPaise, onClose, onSubmit }) {
  const idemKey = useRef(newKey());
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [raced, setRaced] = useState(null);
  const [done, setDone] = useState(null);
  // Client hint only — refreshed downward if a concurrent waiver won the headroom.
  const [capHintPaise, setCapHintPaise] = useState(
    netChargesPaise != null ? Math.max(0, Number(netChargesPaise) || 0) : null,
  );

  const amountNum = Number(amount);
  const amountPaise = Math.round((Number.isFinite(amountNum) ? amountNum : 0) * 100);
  const amountValid = Number.isFinite(amountNum) && amountPaise > 0;
  const over = amountValid && capHintPaise != null && amountPaise > capHintPaise;
  const reasonValid = reason.trim().length > 0;
  const valid = amountValid && !over && reasonValid;

  // Waiver lowers what's owed: balance_after = balance − waiver.
  const afterPreview = useMemo(() => {
    if (!amountValid || over) return null;
    return describeBalance((Math.round(Number(balancePaise) || 0)) - amountPaise);
  }, [amountValid, over, amountPaise, balancePaise]);

  const submit = async () => {
    if (!valid || busy) return;
    setBusy(true);
    setError(null);
    setRaced(null);
    const res = await onSubmit({
      amount_paise: amountPaise,
      reason: reason.trim(),
      idempotency_key: idemKey.current,
    });
    setBusy(false);
    if (res && res.success) {
      setDone(res.data);
      return;
    }
    if (res && res.code === 'CONCESSION_EXCEEDS_CHARGES' && res.remaining_paise != null) {
      // The loser of a race — refresh the cap hint from the server, keep the values.
      const nextRem = Math.max(0, Number(res.remaining_paise) || 0);
      setCapHintPaise(nextRem);
      setRaced(nextRem);
      return;
    }
    if (res) {
      setError(
        res.code === 'ADMISSION_NOT_ACTIVE' ? 'Waivers are only allowed while the patient is admitted. After discharge, adjustments happen at final settlement.'
          : res.error || 'Could not record the waiver. Check the values and try again.',
      );
    }
  };

  // ── Success ──────────────────────────────────────────────────────────────
  if (done) {
    const afterBal = describeBalance(done.balance_paise);
    return (
      <div className={overlayCls} data-testid="concession-dialog">
        <div className={panelCls}>
          <div className="px-4 py-3 border-b border-slate-200 dark:border-slate-700 flex items-center">
            <h3 className="text-sm font-semibold text-slate-800 dark:text-slate-100">Waiver recorded</h3>
            <button type="button" onClick={onClose} className="ml-auto text-slate-500 dark:text-slate-400" aria-label="Close">✕</button>
          </div>
          <div className="p-4 space-y-3" data-testid="concession-success">
            <div className="rounded-lg border border-teal-300 dark:border-teal-700 bg-teal-50 dark:bg-teal-900/20 text-teal-800 dark:text-teal-200 text-[13px] p-3">
              Voucher <b className="tabular-nums">{done.voucher_number || '—'}</b>
            </div>
            <div className="rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 px-3 py-2.5 text-[13px] space-y-1">
              <div className="flex justify-between"><span className="text-slate-500 dark:text-slate-400">Waived</span><b className="tabular-nums text-slate-900 dark:text-white">{formatPaise(amountPaise)}</b></div>
              <div className="flex justify-between"><span className="text-slate-500 dark:text-slate-400">{afterBal.label} after waiver</span><b className="tabular-nums text-slate-900 dark:text-white">{afterBal.text}</b></div>
            </div>
            <p className="text-[11px] text-slate-500 dark:text-slate-400">A waiver reduces the amount charged — no money changes hands and deposits already held are unaffected.</p>
          </div>
          <div className="px-4 py-3 border-t border-slate-200 dark:border-slate-700 flex justify-end">
            <button type="button" className={btnSecondary} onClick={onClose} data-testid="concession-done">Done</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={overlayCls} data-testid="concession-dialog">
      <div className={panelCls}>
        <div className="px-4 py-3 border-b border-slate-200 dark:border-slate-700 flex items-center">
          <h3 className="text-sm font-semibold text-slate-800 dark:text-slate-100">Waiver / concession</h3>
          <button type="button" onClick={onClose} className="ml-auto text-slate-500 dark:text-slate-400" aria-label="Close">✕</button>
        </div>
        <div className="p-4 space-y-3">
          <div className="rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 text-[12.5px] text-slate-600 dark:text-slate-300 px-3 py-2">
            <b className="text-slate-900 dark:text-white">{admission.patient_name || admission.patient_id}</b> · {admission.ip_number}
            <div className="mt-1">Waiving a charge reduces the bill — it is not a cash refund. Use “Refund deposit” to return money already collected.</div>
          </div>

          {raced != null && (
            <div className="rounded-lg border border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/20 text-amber-800 dark:text-amber-200 text-[12.5px] p-2.5" data-testid="concession-raced">
              The bill changed in another session — only <b className="tabular-nums">{formatPaise(raced)}</b> can be waived now. The figures above have been refreshed.
            </div>
          )}
          {error && (
            <div className="rounded-lg border border-red-300 dark:border-red-700 bg-red-50 dark:bg-red-900/20 text-red-800 dark:text-red-200 text-[12.5px] p-2.5" data-testid="concession-error">{error}</div>
          )}

          <div>
            <label className={labelCls} htmlFor="cc-amount">Waiver amount (₹)</label>
            <input id="cc-amount" className={`${inputCls} max-w-[180px]`} type="number" min="0" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="0" data-testid="concession-amount" />
            {over && (
              <div className="text-[12px] text-red-600 dark:text-red-400 mt-1.5" data-testid="concession-over-error">Only <b className="tabular-nums">{formatPaise(capHintPaise)}</b> can be waived against this bill’s charges. Enter {formatPaise(capHintPaise)} or less.</div>
            )}
          </div>

          <div>
            <label className={labelCls} htmlFor="cc-reason">Reason <span className="text-red-500">*</span></label>
            <textarea id="cc-reason" className={inputCls} rows={2} maxLength={300} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. Staff-family concession approved by admin" data-testid="concession-reason" />
            <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-1">Required — stamped on the record with your name.</p>
          </div>

          <div className="flex items-center justify-between rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 px-3 py-2.5">
            <span className="text-[13px] text-slate-600 dark:text-slate-300">{afterPreview ? afterPreview.label : 'Balance'} after this waiver</span>
            <b className="text-[15px] tabular-nums text-slate-900 dark:text-white" data-testid="concession-after">{afterPreview ? afterPreview.text : '—'}</b>
          </div>
        </div>
        <div className="px-4 py-3 border-t border-slate-200 dark:border-slate-700 flex justify-end gap-2">
          <button type="button" className={btnSecondary} onClick={onClose}>Cancel</button>
          <button type="button" className={btnPrimary} onClick={submit} disabled={!valid || busy} data-testid="concession-confirm">
            {busy ? 'Saving…' : 'Grant waiver'}
          </button>
        </div>
      </div>
    </div>
  );
}
